import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Palette, Sparkles, Loader2, Download, ImagePlus, X } from "lucide-react";

const STYLES = ["minimalist", "vintage", "playful", "elegant", "hand-drawn", "bold", "watercolor"];

// Downscale + JPEG-compress an image file to fit under ~1MB base64
async function fileToScaledBase64(file, maxDim = 1024, quality = 0.85) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image(); im.onload = () => resolve(im); im.onerror = reject; im.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  const jpegDataUrl = canvas.toDataURL("image/jpeg", quality);
  return { dataUrl: jpegDataUrl, base64: jpegDataUrl.split(",")[1] };
}

export default function LogoMaker() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [bn, setBn] = useState("");
  const [style, setStyle] = useState("minimalist");
  const [colors, setColors] = useState("warm coral and cream");
  const [concept, setConcept] = useState("");
  const [refPreview, setRefPreview] = useState(null);
  const [refBase64, setRefBase64] = useState(null);
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const refInputRef = useRef(null);

  useEffect(() => { if (!loading && (!user || user === false)) nav("/login"); if (user?.username && !bn) setBn(`${user.username}'s Bakery`); /* eslint-disable-next-line */ }, [user, loading]);

  const onPickRef = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please pick an image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    try {
      const { dataUrl, base64 } = await fileToScaledBase64(file);
      setRefPreview(dataUrl); setRefBase64(base64);
    } catch { toast.error("Could not read that image"); }
  };
  const clearRef = () => { setRefPreview(null); setRefBase64(null); if (refInputRef.current) refInputRef.current.value = ""; };

  const gen = async () => {
    if (!bn.trim()) { toast.error("Enter a business name"); return; }
    setBusy(true); setImages([]);
    try {
      const payload = { business_name: bn, style, colors, concept };
      if (refBase64) payload.reference_image_b64 = refBase64;
      const { data } = await api.post("/marketing/logo", payload, { timeout: 180000 });
      setImages(data.images || []);
      if (!data.images?.length) toast.error("No image returned — try again");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (loading || !user) return null;
  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Palette className="w-3.5 h-3.5" style={{ color: "#8E7CC3" }} /><span>Marketing tool · Gemini 3 Pro</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">AI <span style={{ color: "#8E7CC3" }}>Logo & Branding</span> Maker</h1>
          <p className="mt-2 text-[#857163] max-w-2xl">Generate a custom logo for your bakery — perfect for packaging, business cards, and social media profiles. Optionally upload a reference to guide the style.</p>
        </div>
        <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
          <div className="cp-card p-5 space-y-3">
            <label><span className="text-xs text-[#857163]">Business name</span><input className="cp-input mt-1" value={bn} onChange={(e) => setBn(e.target.value)} data-testid="bn" /></label>
            <label><span className="text-xs text-[#857163]">Style</span>
              <select className="cp-input mt-1" value={style} onChange={(e) => setStyle(e.target.value)} data-testid="style-select">{STYLES.map((s) => <option key={s}>{s}</option>)}</select>
            </label>
            <label><span className="text-xs text-[#857163]">Colors</span><input className="cp-input mt-1" value={colors} onChange={(e) => setColors(e.target.value)} placeholder="warm coral and cream" data-testid="colors" /></label>
            <label className="block"><span className="text-xs text-[#857163]">Concept (optional)</span><textarea className="cp-input mt-1 min-h-[80px]" value={concept} onChange={(e) => setConcept(e.target.value)} placeholder="A rolling pin arched over a cupcake, hand-drawn illustration…" data-testid="concept" /></label>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#857163]">Style reference image (optional)</span>
                {refPreview && <button type="button" onClick={clearRef} className="text-[11px] text-[#857163] hover:text-[#3B2C24] inline-flex items-center gap-1" data-testid="ref-clear"><X className="w-3 h-3" /> Remove</button>}
              </div>
              <input ref={refInputRef} type="file" accept="image/*" onChange={(e) => onPickRef(e.target.files?.[0])} className="hidden" data-testid="ref-input" />
              {refPreview ? (
                <div className="relative rounded-2xl border border-[#EBDCD1] overflow-hidden bg-[#F9EFE6]">
                  <img src={refPreview} alt="reference" className="w-full max-h-48 object-contain" data-testid="ref-preview" />
                </div>
              ) : (
                <button type="button" onClick={() => refInputRef.current?.click()} className="w-full rounded-2xl border border-dashed border-[#EBDCD1] px-4 py-4 text-sm text-[#857163] hover:bg-[#F9EFE6] inline-flex items-center justify-center gap-2" data-testid="ref-upload-btn">
                  <ImagePlus className="w-4 h-4" /> Upload style reference
                </button>
              )}
              <div className="text-[11px] text-[#857163] mt-1">Tip: upload a logo/photo whose vibe or color palette you love. We use it as inspiration — not a copy.</div>
            </div>

            <button onClick={gen} disabled={busy || !bn.trim()} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-base disabled:opacity-50" style={{ background: "linear-gradient(135deg, #8E7CC3, #6B5FA8)" }} data-testid="gen-btn">
              {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Designing…</> : <><Sparkles className="w-5 h-5" /> Generate logo</>}
            </button>
            <div className="text-[11px] text-[#857163]">Free tier: 3 logos/month · Premium: unlimited · Powered by Gemini 3 Pro (slower, higher quality)</div>
          </div>
          <div className="cp-card p-5 min-h-[300px] grid place-items-center">
            {busy ? <div className="text-center"><Loader2 className="w-10 h-10 animate-spin text-[#8E7CC3] mx-auto" /><div className="text-xs text-[#857163] mt-2">Gemini 3 Pro can take 30–60s…</div></div> :
              images.length ? (
                <div className="grid grid-cols-1 gap-3 w-full" data-testid="logo-results">
                  {images.map((img, i) => (
                    <div key={i} className="relative">
                      <img src={fileUrl(img)} alt="" className="w-full rounded-2xl border border-[#EBDCD1]" />
                      <a href={fileUrl(img)} download className="absolute top-2 right-2 bg-white/90 rounded-full p-2 hover:scale-105 transition" title="Download"><Download className="w-4 h-4" /></a>
                    </div>
                  ))}
                </div>
              ) : <div className="text-center text-[#857163]"><Palette className="w-12 h-12 mx-auto opacity-40 mb-2" /><div>Your logo will appear here</div></div>
            }
          </div>
        </div>
      </main>
    </>
  );
}
